import {
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  GKButton,
  GKEditor,
  GKTextField,
  Grid2,
  Switch,
  Typography,
} from "components";
import GKCheckbox from "components/GKCheckbox";
import useRevenueManager from "hooks/useRevenueManager";
import DashboardLayout from "layouts/DashboardLayout";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { revenuePlanType } from "utils/constants";
import useDocumentTitle from "utils/useDocumentTitle";
import { validateNotWhitespaceOnly } from "utils/validationSchema";
import AdvanceDeposit from "./AdvanceDeposit";
import ManagementFee from "./ManagementFee";
import PerformanceFee from "./PerformanceFee";
import SubscriptionPlan from "./SubscriptionPlan";

type SubscriptionPlanType = {
  frequency?: string;
  amount?: string;
  discountAmount?: number;
  taxAmount?: string;
  withoutTaxAmount?: string;
};

type FormValues = {
  name?: string;
  description?: string;
  status?: boolean;
  revenueProducts?: string[];
  managementFee?: {
    feeType?: string;
    feeMethod?: string;
    calculationBenchmark?: string;
    calculationFrequency?: string;
    fee?: string;
    billFrequency?: string;
    outOfPocketExpense?: string;
    minimumFee?: number;
    maximumFee?: number;
    highwatermark?: boolean;
    managementFeeSlab?: any[];
    calculationMethod?: string;
    // highwatermarkAmount?: number;
    softHurdle?: number;
    hardHurdle?: number;
    outOfPocketExpenses?: number;
    productName?: string;
    hsnCode?: string;
    minimumInvestment: number;
    availableFrequency: string[];
  };
  performanceFee?: {
    feeType?: string;
    feeMethod?: string;
    calculationBenchmark?: string;
    calculationFrequency?: string;
    fee?: string;
    billFrequency?: string;
    outOfPocketExpense?: string;
    minimumFee?: number;
    maximumFee?: number;
    highwatermark?: boolean;
    performanceFeeSlab?: any[];
    calculationMethod?: string;
    // highwatermarkAmount?: number;
    softHurdle?: boolean;
    hardHurdle?: boolean;
    outOfPocketExpenses?: number;
    minesManagementFee?: boolean;
    productName?: string;
    hsnCode?: string;
    hurdle?: number;
    includeDividend: boolean;
  };
  brokerage?: {
    brokerage?: number;
    transaction?: string;
  };
  exitLoad?: {
    exitCharge?: number;
    minimumInvestmentTime?: string;
  };
  subscriptionFee?: {
    isFree: boolean;
    productName?: string;
    hsnCode?: string;
    subscriptionPlan: SubscriptionPlanType[];
  };
  advanceDeposit?: {
    productName: string;
    hsnCode: string;
    fee: string;
    feeType: string;
  };
};

const CreateFeePlans = () => {
  useDocumentTitle("Create Fee Plan");

  const { createFeePlan } = useRevenueManager();

  const form = useForm<FormValues>({
    defaultValues: {
      name: "",
      description: "",
      status: true,
      revenueProducts: [],
      managementFee: {
        feeType: "",
        feeMethod: "simple",
        calculationBenchmark: "",
        fee: "",
        calculationFrequency: "",
        billFrequency: "",
        highwatermark: false,
        minimumFee: 0,
        maximumFee: 0,
        outOfPocketExpenses: 0,
        // managementFeeSlab: [{ lowerLimit: "", upperLimit: "", slabFee: "" }],
        // highwatermarkAmount: 0,
        calculationMethod: "",
        softHurdle: 0,
        hardHurdle: 0,
        productName: "",
        hsnCode: "",
        minimumInvestment: 0,
        availableFrequency: [],
      },
      performanceFee: {
        feeType: "aum_based",
        feeMethod: "simple",
        calculationBenchmark: "",
        fee: "",
        calculationFrequency: "",
        billFrequency: "",
        highwatermark: true,
        minimumFee: 0,
        maximumFee: 0,
        outOfPocketExpenses: 0,
        // performanceFeeSlab: [{ lowerLimit: "", upperLimit: "", slabFee: "" }],
        // highwatermarkAmount: 0,
        calculationMethod: "trailing",
        softHurdle: false,
        hardHurdle: false,
        minesManagementFee: false,
        productName: "",
        hsnCode: "",
        hurdle: 0,
        includeDividend: true,
      },
      brokerage: {
        brokerage: 0,
        transaction: "",
      },
      exitLoad: {
        exitCharge: 0,
        minimumInvestmentTime: "",
      },
      subscriptionFee: {
        isFree: false,
        productName: "",
        hsnCode: "",
        subscriptionPlan: [
          {
            frequency: "",
            amount: "",
            discountAmount: 0,
            taxAmount: "",
            withoutTaxAmount: "",
          },
        ],
      },
      advanceDeposit: {
        productName: "Advance Fee",
        hsnCode: "998311",
        fee: "",
        feeType: "",
      },
    },
  });

  const onSubmit = (data: any) => {
    const revenueProductMappings = {
      management_fee: "managementFee",
      performance_fee: "performanceFee",
      brokerage: "brokerage",
      exit_load: "exitLoad",
      subscription_fee: "subscriptionFee",
      advance_deposit: "advanceDeposit",
    };

    const newValue: any = {
      name: data.name,
      revenueProducts: data.revenueProducts,
      status: data.status,
      description: data?.description,
    };

    data.revenueProducts.forEach(
      (product: keyof typeof revenueProductMappings) => {
        const field = revenueProductMappings[product];
        if (field && data[field]) {
          newValue[field] = data[field];
        }
      },
    );

    createFeePlan(newValue);
  };

  return (
    <DashboardLayout title="Create Fee Plan">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Grid2 container spacing={1}>
            <Grid2
              size={12}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              my={1}
            >
              <Typography fontSize={18} fontWeight={600}>
                Set Up Fee Plan
              </Typography>
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="name"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter name",
                  },
                  validate: validateNotWhitespaceOnly,
                }}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    requiredField
                    inputLabel="Fee Plan Name"
                    name="name"
                    error={Boolean(form?.formState?.errors?.name)}
                    helperText={form?.formState?.errors?.name?.message}
                  />
                )}
              />
            </Grid2>
            <Grid2 display={"flex"} alignItems={"center"}>
              <Controller
                name="status"
                control={form.control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        color="primary"
                      />
                    }
                    label="Active"
                    labelPlacement="end"
                  />
                )}
              />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                name="description"
                control={form.control}
                render={({ field }) => (
                  <GKEditor
                    value={field.value}
                    onChange={field.onChange}
                    inputLabel="Description"
                  />
                )}
              />
            </Grid2>
            <Grid2 size={12}>
              <FormGroup row>
                {revenuePlanType.map((option) => (
                  <Controller
                    key={option.value}
                    name="revenueProducts"
                    control={form.control}
                    render={({ field: { onChange, value } }) => {
                      const handleCheckboxChange = (
                        e: React.ChangeEvent<HTMLInputElement>,
                        optionValue: string,
                      ) => {
                        const isChecked = e.target.checked;

                        if (
                          ["subscription_fee", "advance_deposit"].includes(
                            optionValue,
                          )
                        ) {
                          onChange(isChecked ? [optionValue] : []);
                        } else {
                          const updatedValue = isChecked
                            ? [
                                ...value.filter(
                                  (item) =>
                                    ![
                                      "subscription_fee",
                                      "advance_deposit",
                                    ].includes(item),
                                ),
                                optionValue,
                              ]
                            : value.filter((item) => item !== optionValue);

                          onChange(updatedValue);
                        }
                      };
                      return (
                        <FormControl component="fieldset">
                          <FormControlLabel
                            key={option.value}
                            control={
                              <GKCheckbox
                                name="revenueProducts"
                                checked={value.includes(option.value)}
                                onChange={(e) =>
                                  handleCheckboxChange(e, option.value)
                                }
                              />
                            }
                            label={option.name}
                          />
                        </FormControl>
                      );
                    }}
                  />
                ))}
              </FormGroup>
            </Grid2>

            {form.watch("revenueProducts").includes("management_fee") && (
              <>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
                <ManagementFee />
              </>
            )}
            {form.watch("revenueProducts").includes("performance_fee") && (
              <>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
                <PerformanceFee />
              </>
            )}
            {form.watch("revenueProducts").includes("subscription_fee") && (
              <>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
                <SubscriptionPlan />
              </>
            )}
            {form.watch("revenueProducts").includes("advance_deposit") && (
              <>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
                <AdvanceDeposit />
              </>
            )}

            {/* {form.watch("revenueProducts").includes("brokerage") && (
              <>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
                <Brokerage />
              </>
            )}
            {form.watch("revenueProducts").includes("exit_Loan") && (
              <>
                <Grid2 size={12}>
                  <Divider />
                </Grid2>
                <ExitLoad />
              </>
            )} */}
            {form.getValues("revenueProducts").length !== 0 && (
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"flex-end"}
                mt={2}
              >
                <GKButton variant="contained" type="submit" size="medium">
                  Submit
                </GKButton>
              </Grid2>
            )}
          </Grid2>
        </form>
      </FormProvider>
    </DashboardLayout>
  );
};

export default CreateFeePlans;
